import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CategoryProductSelector from '../components/CategoryProductSelector';
import { useAdminScope } from '../contexts/AdminScopeContext';
import categoryService from '../services/categoryService';
import productService from '../services/productService';
import type { Product } from '../models/Product';
import type { Category } from '../models/Category';
import {
  DEFAULT_PRODUCT_AVAILABILITY_RESET_GLOBAL_CONFIG,
  ProductAvailabilityResetGlobalConfig,
  ProductAvailabilityResetRuntimeState,
  ProductAvailabilityResetStoreConfig,
  ProductResetSelectionMode,
  getProductAvailabilityResetGlobalConfig,
  getProductAvailabilityResetRuntimeState,
  getProductAvailabilityResetStoreConfig,
  listProductAvailabilityResetStoreConfigs,
  updateProductAvailabilityResetGlobalConfig,
  updateProductAvailabilityResetStoreConfig,
} from '../services/productAvailabilityResetService';

const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Dimanche' },
  { value: 1, label: 'Lundi' },
  { value: 2, label: 'Mardi' },
  { value: 3, label: 'Mercredi' },
  { value: 4, label: 'Jeudi' },
  { value: 5, label: 'Vendredi' },
  { value: 6, label: 'Samedi' },
];

const SELECTION_MODE_OPTIONS: Array<{ value: ProductResetSelectionMode; label: string }> = [
  { value: 'all', label: 'Tous les produits du store' },
  { value: 'only_selected', label: 'Seulement les produits sélectionnés' },
  { value: 'all_except_selected', label: 'Tous sauf les produits sélectionnés' },
];

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

const ProductAvailabilityResetSettings: React.FC = () => {
  const {
    loading: scopeLoading,
    activeScopeType,
    selectedStoreId,
    selectedStore,
    stores,
  } = useAdminScope();

  const [loading, setLoading] = React.useState(true);
  const [savingGlobal, setSavingGlobal] = React.useState(false);
  const [savingStore, setSavingStore] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  const [globalConfig, setGlobalConfig] =
    React.useState<ProductAvailabilityResetGlobalConfig>(
      DEFAULT_PRODUCT_AVAILABILITY_RESET_GLOBAL_CONFIG
    );
  const [runtimeState, setRuntimeState] =
    React.useState<ProductAvailabilityResetRuntimeState | null>(null);
  const [storeConfigsById, setStoreConfigsById] = React.useState<
    Record<string, ProductAvailabilityResetStoreConfig>
  >({});

  const [editorStoreId, setEditorStoreId] = React.useState<string>('');
  const [editorStoreConfig, setEditorStoreConfig] =
    React.useState<ProductAvailabilityResetStoreConfig | null>(null);
  const [storeProducts, setStoreProducts] = React.useState<Product[]>([]);
  const [storeCategories, setStoreCategories] = React.useState<Category[]>([]);
  const [loadingStoreConfig, setLoadingStoreConfig] = React.useState(false);
  const [loadingStoreProducts, setLoadingStoreProducts] = React.useState(false);

  const effectiveStoreId =
    activeScopeType === 'store' && selectedStoreId ? selectedStoreId : editorStoreId;

  const selectedStoreLabel =
    stores.find((store) => store.id === effectiveStoreId)?.name || selectedStore?.name || null;

  const loadStoreEditor = React.useCallback(
    async (storeId: string, global: ProductAvailabilityResetGlobalConfig) => {
      if (!storeId) {
        setEditorStoreConfig(null);
        setStoreProducts([]);
        setStoreCategories([]);
        return;
      }

      setLoadingStoreConfig(true);
      setLoadingStoreProducts(true);

      try {
        const [config, products, categories] = await Promise.all([
          getProductAvailabilityResetStoreConfig(storeId),
          productService.getProducts({ storeId }),
          categoryService.getCategories({ storeId }),
        ]);

        setEditorStoreConfig(
          config || {
            storeId,
            enabled: global.defaultStoreEnabled,
            selectionMode: 'all',
            productIds: [],
          }
        );
        setStoreProducts(products);
        setStoreCategories(categories);
      } catch (e: any) {
        setError(e?.message || 'Erreur lors du chargement du reset produit pour ce store.');
      } finally {
        setLoadingStoreConfig(false);
        setLoadingStoreProducts(false);
      }
    },
    []
  );

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const [nextGlobal, nextRuntime, storeConfigs] = await Promise.all([
        getProductAvailabilityResetGlobalConfig(),
        getProductAvailabilityResetRuntimeState(),
        listProductAvailabilityResetStoreConfigs(),
      ]);

      setGlobalConfig(nextGlobal);
      setRuntimeState(nextRuntime);
      setStoreConfigsById(
        storeConfigs.reduce<Record<string, ProductAvailabilityResetStoreConfig>>((acc, config) => {
          acc[config.storeId] = config;
          return acc;
        }, {})
      );

      const initialStoreId =
        (activeScopeType === 'store' && selectedStoreId) ||
        editorStoreId ||
        stores[0]?.id ||
        '';

      if (initialStoreId) {
        setEditorStoreId(initialStoreId);
        await loadStoreEditor(initialStoreId, nextGlobal);
      } else {
        setEditorStoreConfig(null);
        setStoreProducts([]);
        setStoreCategories([]);
      }
    } catch (e: any) {
      setError(e?.message || 'Erreur lors du chargement des paramètres de reset.');
    } finally {
      setLoading(false);
    }
  }, [activeScopeType, editorStoreId, loadStoreEditor, selectedStoreId, stores]);

  React.useEffect(() => {
    if (!scopeLoading) {
      void load();
    }
  }, [load, scopeLoading]);

  React.useEffect(() => {
    if (activeScopeType === 'store' && selectedStoreId && selectedStoreId !== editorStoreId) {
      setEditorStoreId(selectedStoreId);
      void loadStoreEditor(selectedStoreId, globalConfig);
    }
  }, [activeScopeType, editorStoreId, globalConfig, loadStoreEditor, selectedStoreId]);

  const handleSaveGlobal = async () => {
    setSavingGlobal(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await updateProductAvailabilityResetGlobalConfig(globalConfig);
      setGlobalConfig(updated);
      setSuccess('Configuration globale du reset enregistrée.');

      if (effectiveStoreId) {
        await loadStoreEditor(effectiveStoreId, updated);
      }
    } catch (e: any) {
      setError(e?.message || 'Erreur lors de l’enregistrement de la configuration globale.');
    } finally {
      setSavingGlobal(false);
    }
  };

  const handleSaveStore = async () => {
    if (!effectiveStoreId || !editorStoreConfig) {
      return;
    }

    setSavingStore(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await updateProductAvailabilityResetStoreConfig(effectiveStoreId, {
        enabled: editorStoreConfig.enabled,
        selectionMode: editorStoreConfig.selectionMode,
        productIds: editorStoreConfig.productIds,
      });

      setEditorStoreConfig(updated);
      setStoreConfigsById((prev) => ({
        ...prev,
        [effectiveStoreId]: updated,
      }));
      setSuccess(`Configuration store enregistrée pour ${selectedStoreLabel || effectiveStoreId}.`);
    } catch (e: any) {
      setError(e?.message || 'Erreur lors de l’enregistrement du store.');
    } finally {
      setSavingStore(false);
    }
  };

  const effectiveStoreEnabled = (storeId: string): boolean =>
    storeConfigsById[storeId]?.enabled ?? globalConfig.defaultStoreEnabled;

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h6" gutterBottom>
          Reset disponibilité produits
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Cette configuration pilote la Cloud Function `resetProductAvailabilityDaily`. Le reset ne
          réactive plus aveuglément les produits: il remet `isAvailable` en cohérence avec
          `isActive` et `isVisibleOnline`.
        </Typography>
      </Box>

      {loading ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={18} />
          <Typography>Chargement…</Typography>
        </Stack>
      ) : (
        <>
          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Typography variant="subtitle1">Planification globale</Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={globalConfig.enabled}
                    onChange={(_, checked) =>
                      setGlobalConfig((prev) => ({ ...prev, enabled: checked }))
                    }
                  />
                }
                label={globalConfig.enabled ? 'Reset global activé' : 'Reset global désactivé'}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={globalConfig.defaultStoreEnabled}
                    onChange={(_, checked) =>
                      setGlobalConfig((prev) => ({ ...prev, defaultStoreEnabled: checked }))
                    }
                  />
                }
                label="Activer le reset par défaut sur les stores non configurés explicitement"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={globalConfig.excludePromoProducts}
                    onChange={(_, checked) =>
                      setGlobalConfig((prev) => ({ ...prev, excludePromoProducts: checked }))
                    }
                  />
                }
                label="Exclure les produits PROMO du reset"
              />

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel id="product-reset-schedule-mode-label">Rythme</InputLabel>
                  <Select
                    labelId="product-reset-schedule-mode-label"
                    label="Rythme"
                    value={globalConfig.scheduleMode}
                    onChange={(event) =>
                      setGlobalConfig((prev) => ({
                        ...prev,
                        scheduleMode: event.target.value as ProductAvailabilityResetGlobalConfig['scheduleMode'],
                      }))
                    }
                  >
                    <MenuItem value="daily">Tous les jours</MenuItem>
                    <MenuItem value="weekly">Hebdomadaire</MenuItem>
                  </Select>
                </FormControl>

                {globalConfig.scheduleMode === 'weekly' && (
                  <FormControl fullWidth>
                    <InputLabel id="product-reset-weekday-label">Jour</InputLabel>
                    <Select
                      labelId="product-reset-weekday-label"
                      label="Jour"
                      value={String(globalConfig.weeklyDay)}
                      onChange={(event) =>
                        setGlobalConfig((prev) => ({
                          ...prev,
                          weeklyDay: Number(event.target.value),
                        }))
                      }
                    >
                      {WEEKDAY_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={String(option.value)}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <FormControl fullWidth>
                  <InputLabel id="product-reset-hour-label">Heure</InputLabel>
                  <Select
                    labelId="product-reset-hour-label"
                    label="Heure"
                    value={String(globalConfig.hour)}
                    onChange={(event) =>
                      setGlobalConfig((prev) => ({
                        ...prev,
                        hour: Number(event.target.value),
                      }))
                    }
                  >
                    {HOURS.map((hour) => (
                      <MenuItem key={hour} value={String(hour)}>
                        {String(hour).padStart(2, '0')}:00
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              <TextField
                fullWidth
                label="Fuseau horaire"
                value={globalConfig.timeZone}
                onChange={(event) =>
                  setGlobalConfig((prev) => ({ ...prev, timeZone: event.target.value }))
                }
                helperText="Choisis le fuseau horaire utilisé pour déclencher le reset au bon moment."
              />

              <Box>
                <Button variant="contained" onClick={handleSaveGlobal} disabled={savingGlobal}>
                  {savingGlobal ? 'Enregistrement…' : 'Enregistrer la planification'}
                </Button>
              </Box>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Typography variant="subtitle1">État runtime</Typography>
              {runtimeState ? (
                <>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {runtimeState.lastRunKey && <Chip label={`Dernier slot: ${runtimeState.lastRunKey}`} />}
                    {runtimeState.updatedCount !== undefined && (
                      <Chip label={`Produits mis à jour: ${runtimeState.updatedCount}`} />
                    )}
                    {runtimeState.affectedStoreCount !== undefined && (
                      <Chip label={`Stores traités: ${runtimeState.affectedStoreCount}`} />
                    )}
                    {runtimeState.skippedPromoCount !== undefined && (
                      <Chip label={`Promos ignorées: ${runtimeState.skippedPromoCount}`} />
                    )}
                  </Stack>
                  {runtimeState.lastRunAt && (
                    <Typography variant="body2" color="text.secondary">
                      Dernière exécution: {runtimeState.lastRunAt.toLocaleString('fr-FR')}
                    </Typography>
                  )}
                  {runtimeState.lastError && (
                    <Alert severity="warning">
                      Dernière erreur: {runtimeState.lastError}
                    </Alert>
                  )}
                </>
              ) : (
                <Alert severity="info">Aucune exécution enregistrée pour le moment.</Alert>
              )}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Typography variant="subtitle1">Configuration par store</Typography>

              {activeScopeType !== 'store' && (
                <FormControl fullWidth>
                  <InputLabel id="product-reset-store-label">Store à éditer</InputLabel>
                  <Select
                    labelId="product-reset-store-label"
                    label="Store à éditer"
                    value={editorStoreId}
                    onChange={(event) => {
                      const nextStoreId = event.target.value;
                      setEditorStoreId(nextStoreId);
                      void loadStoreEditor(nextStoreId, globalConfig);
                    }}
                  >
                    {stores.map((store) => (
                      <MenuItem key={store.id} value={store.id}>
                        {store.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {stores.map((store) => (
                  <Chip
                    key={store.id}
                    color={effectiveStoreEnabled(store.id) ? 'success' : 'default'}
                    label={`${store.name}: ${effectiveStoreEnabled(store.id) ? 'activé' : 'désactivé'}`}
                    variant={effectiveStoreId === store.id ? 'filled' : 'outlined'}
                  />
                ))}
              </Stack>

              {!effectiveStoreId ? (
                <Alert severity="info">Sélectionne un store pour configurer le reset ciblé.</Alert>
              ) : loadingStoreConfig || loadingStoreProducts ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={18} />
                  <Typography>Chargement du store…</Typography>
                </Stack>
              ) : editorStoreConfig ? (
                <>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editorStoreConfig.enabled ?? globalConfig.defaultStoreEnabled}
                        onChange={(_, checked) =>
                          setEditorStoreConfig((prev) =>
                            prev ? { ...prev, enabled: checked } : prev
                          )
                        }
                      />
                    }
                    label="Activer le reset sur ce store"
                  />

                  <FormControl fullWidth>
                    <InputLabel id="product-reset-selection-mode-label">Périmètre produits</InputLabel>
                    <Select
                      labelId="product-reset-selection-mode-label"
                      label="Périmètre produits"
                      value={editorStoreConfig.selectionMode}
                      onChange={(event) =>
                        setEditorStoreConfig((prev) =>
                          prev
                            ? {
                                ...prev,
                                selectionMode: event.target.value as ProductResetSelectionMode,
                              }
                            : prev
                        )
                      }
                    >
                      {SELECTION_MODE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {editorStoreConfig.selectionMode !== 'all' && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {editorStoreConfig.selectionMode === 'only_selected'
                          ? 'Produits à reset'
                          : 'Produits exclus du reset'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {editorStoreConfig.selectionMode === 'only_selected'
                          ? 'Sélectionne les produits que le reset doit réactiver sur ce store.'
                          : 'Sélectionne les produits à ignorer lors du reset sur ce store.'}
                      </Typography>
                      <CategoryProductSelector
                        categories={storeCategories}
                        products={storeProducts}
                        selectedProductIds={editorStoreConfig.productIds}
                        onSelectionChange={(productIds) =>
                          setEditorStoreConfig((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  productIds,
                                }
                              : prev
                          )
                        }
                      />
                    </Paper>
                  )}

                  <Typography variant="body2" color="text.secondary">
                    Produits chargés pour ce store: {storeProducts.length}. Produits sélectionnés:{' '}
                    {editorStoreConfig.productIds.length}.
                  </Typography>

                  <Box>
                    <Button variant="contained" onClick={handleSaveStore} disabled={savingStore}>
                      {savingStore ? 'Enregistrement…' : 'Enregistrer la configuration store'}
                    </Button>
                  </Box>
                </>
              ) : null}
            </Stack>
          </Paper>
        </>
      )}
    </Stack>
  );
};

export default ProductAvailabilityResetSettings;
